import React, { useState, useRef } from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  VStack, 
  HStack, 
  Separator, // Using Separator
  Badge, 
  Button, 
  SimpleGrid, 
  Image,
} from "@chakra-ui/react";
import { toast, Flip } from "react-toastify";
import * as Icon from "@phosphor-icons/react";
import moment from "moment";

import Invoice from './Invoice';
import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";

const getStatusColor = (status) => {
  const normalizedStatus = status?.toLowerCase();
  
  switch (normalizedStatus) {
    case 'delivered':
    case 'paid':
    case 'picked_up': 
      return '#8dbd61';

    case 'shipped':
    case 'confirmed':
    case 'ready_to_ship':
    case 'in_transit':
    case 'processed':
      return '#2b92e4';

    case 'pending':
    case 'order_placed':
    case 'unpaid':
      return '#F97350';

    case 'cancelled':
    case 'returned':
    case 'undelivered':
      return '#E53E3E';

    default:
      return 'gray';
  }
};


export default function OrderView({ order, onClose }) {
  
  const orderAddress = order.address || {};
  const orderProducts = order.items || [];
  
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const targetRef = useRef(null);

  const waitForImagesToLoad = (imageUrls) => {
    const promises = imageUrls?.map((url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(url);
        img.src = url;
      });
    });

    return Promise.all(promises);
  };

  const toPDF = () => {
    const toastId = toast.info("Generating Invoice, please wait...", {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
        progress: undefined,
        transition: Flip,
    });
    
    const imageList = orderProducts.map(item => 
        item.variant?.thumb_images?.[0]?.url || 
        item.variant?.images?.[0]?.url
    ).filter(url => url);


    waitForImagesToLoad(imageList)
    .then(() => {
        if(targetRef.current) {
            targetRef.current.style.display = "block";
            const element = targetRef.current;
            
            html2canvas(element).then(function(canvas) {
                const imgData = canvas.toDataURL('image/jpeg', 0.9);
                const pdf = new jsPDF({
                    orientation: 'p',
                    unit: 'mm',
                    format: 'a4'
                });
                
                pdf.addImage(imgData, 'JPEG', 10, 10, 190, 277, undefined, 'FAST');
                
                window.open(pdf.output('bloburl'), '_blank');
                
                targetRef.current.style.display = "none";
                
                toast.update(toastId, {
                    render: "Invoice Generated Successfully!",
                    type: "success",
                    autoClose: 3000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    draggable: true,
                });
            });
        }
    })
    .catch(() => {
        toast.update(toastId, {
            render: "Error: Failed to generate invoice or load images.",
            type: "error",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            draggable: true,
        });
        if(targetRef.current) {
            targetRef.current.style.display = "none";
        }
    });
  };

  return (
    <>
    {/* todo : add invoice component */}
      {/* <Invoice 
        ref={targetRef} 
        order={{ 
          ...order, 
          OrderId: order.id, 
          FinalPrice: order.final_total,
          InsertedDate: order.created_at,
          order_address: orderAddress,
          order_products: orderProducts.map(item => ({
             ...item,
             product_image: {
                 ImageUrl: item.variant?.thumb_images?.[0]?.url || item.variant?.images?.[0]?.url
             },
             product_name: item.product?.name,
             Price: item.price,
             Quantity: item.quantity
          }))
        }} 
        style={{display: "none"}} 
      /> */}

      <Box p={6} maxW="container.lg" mx="auto" bg="gray.50" minH="100vh">
        
        <Flex justify="space-between" align="center" mb={6} borderBottom="2px solid" borderColor="red.500" pb={3}>
          <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="extrabold" color="gray.800">
            Order #{order.order_number}
          </Text>
          <HStack spacing={3}>
            <Button 
              size="sm" 
              variant="outline" 
              colorScheme="red"
              leftIcon={<Icon.Printer size={18} />} 
              onClick={toPDF}
            >
              Invoice
            </Button>
            <Button 
              size="sm" 
              colorScheme="red" 
              variant="solid" 
              onClick={onClose}
            >
              Close
            </Button>
          </HStack>
        </Flex>

        <VStack spacing={6} align="stretch" p={5} bg="white" borderRadius="lg" shadow="md" mb={6}>
          
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
                <Badge 
                     bg={getStatusColor(order.status)} 
                    fontSize="sm" 
                    px={3} 
                    py={1} 
                    borderRadius="full"
                    textTransform="uppercase"
                >
                    {order.status}
                </Badge>
                {/* <VStack align="start" spacing={0}>
                    <Text fontSize="sm" color="gray.500">
                        Payment Status: 
                        <Text as="span" fontWeight="semibold" ml={1} color={getStatusColor(order.payment?.status)}>
                            {order.payment?.status}
                        </Text>
                    </Text>
                </VStack> */}
            </HStack>
            
            <HStack spacing={2}>
              {isHelpOpen ? (
                <>
                  <Button size="sm" variant="ghost" colorScheme="green" leftIcon={<Icon.WhatsappLogo size={16} />}>
                    Whatsapp
                  </Button>
                  <Button size="sm" variant="ghost" colorScheme="blue" leftIcon={<Icon.Envelope size={16} />}>
                    Email
                  </Button>
                  <Button size="sm" variant="ghost" colorScheme="gray" leftIcon={<Icon.Phone size={16} />}>
                    Phone
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" colorScheme="red" onClick={() => setIsHelpOpen(true)} leftIcon={<Icon.Question size={16} />}>
                  Need Help?
                </Button>
              )}
            </HStack>
          </Flex>

          <Separator />
          
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} fontSize="sm">
            <VStack align="start" spacing={0}>
              <Text color="gray.500">Order Placed</Text>
              <Text fontWeight="semibold">{moment(order.created_at).format('DD MMM YYYY')}</Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text color="gray.500">Order Value</Text>
              <Text fontWeight="bold" color="red.600">₹ {order.final_total.toFixed(2)}</Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text color="gray.500">Payment Method</Text>
              <Text fontWeight="semibold" textTransform="capitalize">{order.payment?.method || 'N/A'}</Text>
            </VStack>
            <VStack align="start" spacing={0}>
                <Text color="gray.500">Payment Status</Text>
                <Text fontWeight="semibold" textTransform="capitalize" color={getStatusColor(order.payment?.status)}>
                    {order.payment?.status || 'N/A'}
                </Text>
            </VStack>
          </SimpleGrid>

        </VStack>

        {/* ADDRESS, ITEMS, AND PRICE */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} gap={4} mb={6}>
            
            <VStack spacing={6} align="stretch" gridColumn={{ base: "span 1", md: "span 2" }}>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6} gap={4} align="start">
                
                    <Box p={5} width={350} maxWidth={350} bg="white" borderRadius="lg" shadow="md">
                        <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">Delivery Address</Text>
                        <Separator mb={4} />
                        <VStack align="start" spacing={1} fontSize="sm"> 
                            <Text fontWeight="bold">{orderAddress.name}</Text>
                            <Text>{orderAddress.address_line1}</Text>
                            {orderAddress.address_line2 && <Text>{orderAddress.address_line2}</Text>}
                            <Text>{orderAddress.city}, {orderAddress.state} - {orderAddress.postal_code}</Text>
                            <Text color="gray.600" pt={2}>Phone: {orderAddress.phone}</Text> 
                        </VStack>
                    </Box>

                    <Box p={5}  width={350} maxWidth={350} bg="white" borderRadius="lg" shadow="md">
                        <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">Billing Address</Text>
                        <Separator mb={4} />
                        <VStack align="start" spacing={1} fontSize="sm">
                            <Text fontWeight="bold">{orderAddress.name}</Text>
                            <Text>{orderAddress.address_line1}</Text>
                            {orderAddress.address_line2 && <Text>{orderAddress.address_line2}</Text>}
                            <Text>{orderAddress.city}, {orderAddress.state} - {orderAddress.postal_code}</Text>
                            <Text color="gray.600" pt={2}>Email: {orderAddress.email}</Text> 
                        </VStack>
                    </Box>
                </SimpleGrid>

               
            </VStack>

            <Box p={5} bg="white" borderRadius="lg" shadow="xl" borderTop="4px solid" borderColor="red.500">
                <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.700">Price Breakdown</Text>
                <VStack spacing={3} align="stretch"> 
                    
                    <Flex justify="space-between" fontSize="md">
                        <Text color="gray.600">Subtotal</Text>
                        <Text fontWeight="medium">₹ {order.items.reduce((sum, item) => sum + item.variant.orignial_price * item.quantity, 0).toFixed(2)}</Text>
                    </Flex>
                     <Flex justify="space-between" fontSize="md" pb={2}>
                        <Text color="gray.600">Discount</Text>
                        <Text fontWeight="medium" color="red.500">
                            - ₹ {(order.items.reduce((sum, item) => sum + (item.variant.orignial_price  * item.quantity), 0) - order.total_amount).toFixed(2)}
                        </Text>
                    </Flex>
                    <Flex justify="space-between" fontSize="md">
                        <Text color="gray.600">Shipping Fee</Text>
                        <Text fontWeight="medium" color={order.shipping_fee > 0 ? "gray.700" : "green.500"}>
                            {order.shipping_fee > 0 ? `+ ₹ ${order.shipping_fee.toFixed(2)}` : 'FREE'}
                        </Text>
                    </Flex>
                    
                   
                    
                    <Separator my={3} borderColor="gray.300" />
                    
                    <Flex justify="space-between" fontSize="xl" fontWeight="extrabold" pt={2}>
                        <Text>Grand Total</Text>
                        <Text color="red.600">₹ {order.final_total.toFixed(2)}</Text>
                    </Flex>
                </VStack>
            </Box>
        </SimpleGrid>
         <Box p={6}    bg="white" borderRadius="lg" shadow="md"> 
                    <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.700">Order Items ({orderProducts.length})</Text>
                    <VStack Separator={<Separator borderColor="gray.100" />} spacing={5} align="stretch"> 
                        {orderProducts.map((item, index) => {
                            const imageUrl = item.variant?.thumb_images?.[0]?.url || '/placeholder.png';
                            
                            return (
                                <HStack key={index} spacing={5} align="center">
                                    <Image
                                        src={imageUrl}
                                        alt={item.product?.name}
                                        boxSize="120px" 
                                        objectFit="contain"
                                        borderRadius="lg"
                                        fallbackSrc="/placeholder.png"
                                    />
                                    <Box flex="1">
                                        <Text fontWeight="semibold" isTruncated fontSize="sm" >{item.product?.name}</Text>
                                        <Text fontSize="sm" color="gray.500">
                                            {item.variant?.size?.name} / {item.variant?.color?.name}
                                        </Text>
                                    </Box>
                                    <Text fontWeight="medium" w="80px" textAlign="center">
                                        Qty: {item.quantity}
                                    </Text>
                                    <Text fontWeight="bold" color="gray.700" w="100px" textAlign="right">
                                        ₹ {(item.quantity * item.price).toFixed(2)}
                                    </Text>
                                </HStack>
                            );
                        })}
                    </VStack>
                </Box>
      </Box>
    </>
  );
}